import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogInner,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { PasswordToggleIcon } from "@/components/ui/eye-icons";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface InputField {
  id: string;
  label: string;
  type?: "text" | "password" | "email" | "url";
  placeholder?: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
}

interface IntegrationModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  integrationName: string;
  icon: React.ReactNode;
  description: string | React.ReactNode;
  inputs: InputField[];
  onConnect?: (values: Record<string, string>) => void;
  onCancel?: () => void;
  connectButtonText?: string;
  cancelButtonText?: string;
  loading?: boolean;
  errors?: string[];
  className?: string;
}

function IntegrationModal({
  open,
  onOpenChange,
  integrationName,
  icon,
  description,
  inputs,
  onConnect,
  onCancel,
  connectButtonText = "Connect",
  cancelButtonText = "Cancel",
  loading = false,
  errors = [],
  className,
}: IntegrationModalProps) {
  // Track password visibility for each input
  const [passwordVisibility, setPasswordVisibility] = React.useState<
    Record<string, boolean>
  >({});

  const togglePasswordVisibility = (inputId: string) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [inputId]: !prev[inputId],
    }));
  };

  // Check if input is an API key/token field
  const isPasswordField = (input: InputField) => {
    const sensitiveFields = ["api-token", "api-key", "token", "key", "secret"];
    return (
      input.type === "password" ||
      sensitiveFields.some(
        (field) =>
          input.id.toLowerCase().includes(field) ||
          input.label.toLowerCase().includes(field),
      )
    );
  };

  const handleConnect = () => {
    if (onConnect) {
      const values = inputs.reduce(
        (acc, input) => {
          acc[input.id] = input.value;
          return acc;
        },
        {} as Record<string, string>,
      );
      onConnect(values);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange?.(false);
  };

  // Check if all required inputs have values
  const isConnectDisabled = inputs
    .filter((input) => input.required !== false)
    .some((input) => !input.value.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className={cn(
            "max-w-[600px] w-full rounded-md border border-neutral-300",
            className,
          )}
        >
          {/* Header */}
          <DialogHeader className="text-left">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center">{icon}</div>
              <DialogTitle>Connect {integrationName}</DialogTitle>
            </div>
          </DialogHeader>

          {/* Description */}
          <DialogInner>
            <div className="flex flex-col gap-3">
              {typeof description === "string" ? (
                <p className="text-base font-normal leading-snug text-gray-800 m-0">
                  {description}
                </p>
              ) : (
                description
              )}
            </div>

            {/* Input Fields */}
            <div className="flex flex-col gap-6 mt-2">
              {inputs.map((input) => {
                const isPassword = isPasswordField(input);
                const isVisible = passwordVisibility[input.id] || false;
                const inputType = isPassword
                  ? isVisible
                    ? "text"
                    : "password"
                  : input.type || "text";

                return (
                  <div key={input.id} className="flex flex-col gap-3">
                    <label
                      htmlFor={input.id}
                      className="text-base font-semibold leading-snug text-gray-800"
                    >
                      {input.label}
                      {input.required !== false && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    <div className="relative">
                      <Input
                        id={input.id}
                        type={inputType}
                        value={input.value}
                        onChange={(e) => input.onChange(e.target.value)}
                        placeholder={input.placeholder}
                        className={cn(
                          "h-12 px-4 py-3 text-base rounded-md border bg-white",
                          "placeholder:text-gray-400",
                          isPassword && "pr-12", // Add padding for eye icon
                          errors.length > 0
                            ? "border-red-500 focus-visible:border-red-500 focus-visible:ring-1 focus-visible:ring-red-500"
                            : "border-blue-700 focus-visible:border-blue-700 focus-visible:ring-1 focus-visible:ring-blue-700",
                        )}
                      />
                      {isPassword && (
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility(input.id)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label={
                            isVisible ? "Hide password" : "Show password"
                          }
                        >
                          <PasswordToggleIcon isVisible={isVisible} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Error Messages */}
              {errors.length > 0 && (
                <div className="flex flex-col gap-2">
                  {errors.map((error, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-red-600"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="shrink-0"
                      >
                        <path
                          d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8 10.5C7.72 10.5 7.5 10.28 7.5 10V8C7.5 7.72 7.72 7.5 8 7.5C8.28 7.5 8.5 7.72 8.5 8V10C8.5 10.28 8.28 10.5 8 10.5ZM8.5 6H7.5V5H8.5V6Z"
                          fill="currentColor"
                        />
                      </svg>
                      <span className="text-sm font-medium">{error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogInner>
          {/* Actions */}
          <DialogFooter>
            <Button
              intent="primary"
              variant="solid"
              size="md"
              onClick={handleConnect}
              disabled={isConnectDisabled || loading}
              className="px-4"
            >
              {loading ? "Loading..." : connectButtonText}
            </Button>
            <Button
              intent="secondary"
              variant="solid"
              size="md"
              onClick={handleCancel}
              disabled={loading}
              className="px-4"
            >
              {cancelButtonText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

export { IntegrationModal };
export type { InputField, IntegrationModalProps };
